<template>
  <ModalDialog
    ref="dialog"
    :title="title"
    :submitting="submitting"
    :submitTitle="submitTitle"
    :submitDisabled="submitDisabled"
    :showSubmit="submitVisible"
    :scrollable="true"
    icon="fa-calendar-edit"
    @closed="onClosed"
    @submitted="onSubmitted">

    <Spinner v-if="loading" />
    <NotificationBar v-if="loadingError" type="error">
      <NotificationBarTitle>{{loadingError}}</NotificationBarTitle>
    </NotificationBar>

    <FormGroup v-bind:form="taskForm" :labelColumnSize="2" v-show="!loading && !loadingError">
      <TabbedBox ref="tabBox">
        <template v-slot:tablist>
          <Tab :first="true" tabid="tab-task-summary">
            <i class="far fa-calendar"></i> Summary
          </Tab>
          <Tab tabid="tab-task-activity-log" v-if="taskId">
            <i class="far fa-history"></i> Activity Log
          </Tab>
        </template>

        <template v-slot:tabcontent>
          <TabPage :first="true" tabid="tab-task-summary">
            <div class="float-right">
              <ActionDropMenu v-if="!editing">
                <DropdownMenuItem v-if="canUpdateTask" icon="fa-pencil" @click="editing = true">Edit Task</DropdownMenuItem>
                <DropdownMenuItem v-if="canPrintRunSheet" icon="fa-print" @click="displayTaskSheet()">Display Runsheet</DropdownMenuItem>
                <DropdownMenuDivider v-if="canUpdateTask && canPrintRunSheet && canCompleteTask" />
                <DropdownMenuItem v-if="canCompleteTask" @click="flagTaskComplete" icon="fa-flag">Flag Task Completed</DropdownMenuItem>
                <DropdownMenuItem v-if="canCancelTask" @click="cancelTask" icon="fa-times">Cancel Task</DropdownMenuItem>
              </ActionDropMenu>
            </div>
            <FormRow label="Type">
              <FormSelect :options="activeTaskTypeLookup" v-model="taskData.taskItemId" field="taskTypeId" placeholder="Type" v-if="editing" />
              <FormStaticLabel v-if="!editing">{{taskData.taskType ? taskData.taskType.name : ''}}</FormStaticLabel>              
            </FormRow>
            <FormRow label="Status" v-if="taskId">
              <LabelBadge :labelClass="`label-task-status-${taskData.status}`">{{taskData.status | formatLookupName(TaskStatusTypeLookup)}}</LabelBadge>
            </FormRow>
            <FormRow label="Name">
              <FormInputText v-model="taskData.name" v-if="editing" />
              <FormStaticLabel v-if="!editing">{{taskData.name}}</FormStaticLabel>
            </FormRow>
            <FormRow label="Description">
              <FormInputTextArea v-model="taskData.description" v-if="editing" />
              <FormStaticLabel v-if="!editing">{{taskData.description}}</FormStaticLabel>
            </FormRow>
            <FormRow label="Task Start Date">
              <FormDateTime v-model="taskData.scheduleStartDate" field="scheduleStartDate" v-if="editing" />
              <FormStaticLabel v-if="!editing">
                {{taskData.scheduleStartDate | formatDateTime}}
              </FormStaticLabel>
            </FormRow>
            <FormRow label="Task End Date">
              <FormDateTime v-model="taskData.scheduleEndDate" field="scheduleEndDate" v-if="editing" />
              <FormStaticLabel v-if="!editing">
                {{taskData.scheduleEndDate | formatDateTime}}
              </FormStaticLabel>
            </FormRow>
            <FormRow label="Assigned Users">
              <FormMultiSelect :options="userList" v-model="selectedUsers" placeholder="Users" field="users" v-if="editing" />
              <FormStaticLabel v-if="!editing">
                {{selectedUsers | formatMultiSelectLookupName(userList) | formatHyphenEmptyValue}}
              </FormStaticLabel>
            </FormRow>
            <FormRow label="Assigned Resources">
              <FormMultiSelect :options="resourceList" v-model="selectedResources" placeholder="Resources" field="resources" v-if="editing" />
              <FormStaticLabel v-if="!editing">
                {{selectedResources | formatMultiSelectLookupName(resourceList) | formatHyphenEmptyValue}}
              </FormStaticLabel>
            </FormRow>

            <FormHorizontalRule />
            <FormRow label="Alerts" helpTip="Requires at least 1 user resource assigned to the task to send alerts">
              <FormStaticLabel>
                <TaskAlertList v-model="taskData.taskAlerts" :editing="editing" />
              </FormStaticLabel>
            </FormRow>
            
          </TabPage>

          <TabPage tabid="tab-task-activity-log" v-if="taskId">
            <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.task" :linkId="taskId">
              <template v-slot:title><h3>Recent Activity</h3></template>
            </ActivityLogStream>
          </TabPage>
        </template>
      </TabbedBox>
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import Utils from '@/utils/utils'
import Form from '@/mixins/form'
import ColourMixin from '@/mixins/colour'
import PDFMixin from '@/mixins/pdf'

import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'
import UserSearchableSelect from '@/modules/users/components/userSearchableSelect'
import TaskAlertList from '@/modules/tasks/components/taskAlertList'

import { TaskTypes, TaskStatusTypes, TaskStatusTypeLookup, InventoryStockTypes, ResourceTypes, LinkTypes, AccountLabelTypes, OrderStatusTypes, OrderStatusTypeLookup, OrderPaymentTypes } from '@/store/types'
import { TaskRouteLocationTypes, TaskRouteLocationTypeLookup } from '@/store/types'

export default {
  name: 'TaskDialog',
  mixins: [
    ModalDialogMixin,
    Form,
    ColourMixin,
    PDFMixin
  ],
  components: {
    ModalDialog,
    TaskAlertList
  },
  props: {
    taskId: Number,
    isCreateMode: Boolean,
    options: Object
  },
  data() {
    return {
      loading: true,
      loadingError: '',
      accountLocationList: [],
      userList: [],
      resourceList: [],
      taskTypeList: [],

      selectedUsers: [],
      selectedResources: [],

      taskData: {
        name: '',
        taskTypeId: null,
        taskType: {},
        highlightColour: '#4b6584',
        routeStartLocationType: TaskRouteLocationTypes.none,
        routeEndLocationType: TaskRouteLocationTypes.none,
        startPointAccountLocationId: null,
        endPointAccountLocationId: null,
        routeStartLocationAddress: '',
        routeEndLocationAddress: '',
        users: [],
        resources: [],
        taskItems: [],
        taskAlerts: [],
        labelList: []
      },

      scanning: {
        resultMessage: '',
        resultType: '',
        barcode: '',
        submittingOrder: false
      },
      taskForm: {},
      submitting: false,
      deleting: false,
      editing: false,
    }
  },
  watch: {
  },
  created() {
    this.LinkTypes = LinkTypes
    this.TaskRouteLocationTypes = TaskRouteLocationTypes
    this.TaskRouteLocationTypeLookup = TaskRouteLocationTypeLookup
    this.TaskStatusTypeLookup = TaskStatusTypeLookup
  },
  mounted() {
    this.requestData()
  },
  computed: {
    title() {
      if(this.isCreateMode)
        return 'New Task'
      if(this.editing)
        return `Edit Task ${this.taskData.sequenceNumber || ''}`
      return `Task ${this.taskData.sequenceNumber || ''}`
    },
    submitTitle() {
      return this.isCreateMode ? 'Create' : 'Update'
    },
    submitDisabled() {
      if(this.taskData.status == TaskStatusTypes.completed || this.taskData.status == TaskStatusTypes.cancelled)
        return true
      return this.loading
    },
    submitVisible() {
      if(this.taskData.status == TaskStatusTypes.completed || this.taskData.status == TaskStatusTypes.cancelled)
        return false
      
      return this.editing
    },
    canUpdateTask() {
      if(this.taskData.status == TaskStatusTypes.completed || this.taskData.status == TaskStatusTypes.cancelled)
        return false
      return this.userHasUpdatePermission(this.PermissionTypes.TaskPermissionTypes.task)
    },
    canPrintRunSheet() {
      if(this.isCreateMode || this.loading)
        return false
      if(this.taskData.status == TaskStatusTypes.cancelled)
        return false
      return true
    },
    canCompleteTask() {
      if(this.isCreateMode || this.loading)
        return false
      if(this.taskData.status == TaskStatusTypes.completed || this.taskData.status == TaskStatusTypes.cancelled)
        return false
      return this.userHasUpdatePermission(this.PermissionTypes.TaskPermissionTypes.task)
    },
    canCancelTask() {
      if(this.isCreateMode || this.loading)
        return false
      return this.userHasDeletePermission(this.PermissionTypes.TaskPermissionTypes.task)
    },
    activeTaskTypes() {
      return this.taskTypeList.filter(taskType => taskType.isActive)
    },
    activeTaskTypeLookup() {
      return this.activeTaskTypes.map(taskType => {
        return {
          id: taskType.id,
          name: taskType.name
        }
      })
    },
    activityStreamURL() {
      return `/tasks/${this.taskId}/activitystream`
    },
  },
  methods: {
    show(task) {
      // Reset form
      this.taskData = {
        name: '',
        taskTypeId: null,
        highlightColour: '#4b6584',
        scheduleStartDate: moment().toISOString(),
        scheduleEndDate: moment().toISOString(),
        routeStartLocationType: TaskRouteLocationTypes.none,
        routeEndLocationType: TaskRouteLocationTypes.none,
        startPointAccountLocationId: null,
        endPointAccountLocationId: null,
        routeStartLocationAddress: '',
        routeEndLocationAddress: '',
        description: '',
        locationAddress: '',
        users: [],
        resources: [],
        taskItems: [],
        taskAlerts: [],
        labelList: []
      }
      this.taskForm = {}
      this.submitting = false
      this.deleting = false
      this.editing = true

      if(task != null) {
        this.taskData.id = task.id

        if(task.resources) {
          this.selectedResources = task.resources.filter(resource => resource.type == ResourceTypes.general).map(resource => resource.id)
        }
        if(task.users) {
          this.selectedUsers = task.users.map(user => user.id)
        }

        if(task.taskItems)
          this.taskData.taskItems = _.cloneDeep(task.taskItems)

        this.taskData.scheduleStartDate = task.scheduleStartDate || this.taskData.scheduleStartDate
        this.taskData.scheduleEndDate = task.scheduleEndDate || this.taskData.scheduleStartDate

        if(task.id)
          this.editing = false
      }

      // Request the data for the lookup list
      // Also requests task data if editing. Too much info to store in the calendar system
      this.requestData()

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(null)
    },
    onSubmitted() {
      this.taskForm = this.clearFormResponse()

      var taskUpdateData = _.cloneDeep(this.$data.taskData)
      taskUpdateData = _.omit(taskUpdateData, ['routeStartLocationAddress', 'startPointAccountLocationId', 'routeEndLocationAddress', 'endPointAccountLocationId'])
      taskUpdateData.labelList = taskUpdateData.labelList.map(label => label.id)

      taskUpdateData.resources = this.selectedResources
      taskUpdateData.users = this.selectedUsers

      this.submitting = true

      if(this.isCreateMode) {
        this.$apiService.tasksService.createTask(taskUpdateData)
        .then(response => {
          this.submitting = false
          this.modalClosed({ id: response.data.id })
          this.hide()
        })
        .catch(err => {
          this.submitting = false
          this.$data.taskForm = this.processFormResponse(err)
          this.$refs.tabBox.displayErrors()
        })
      } else {
        this.$apiService.tasksService.updateTask(taskUpdateData.id, taskUpdateData)
        .then(response => {
          this.submitting = false
          this.modalClosed({ id: taskUpdateData.id })
          this.hide()
        })
        .catch(err => {
          console.error(err)
          this.submitting = false
          this.$data.taskForm = this.processFormResponse(err)
          this.$refs.tabBox.displayErrors()
        })
      }
    },
    requestData() {
      this.loading = true
      this.loadingError = ''

      var requestList = [
        this.$apiService.tasksService.getTaskTypeList(),
        this.$apiService.tasksService.getResourceList(),
        this.$apiService.userService.getUserList()
      ]
      if(!this.isCreateMode && this.taskData.id)
        requestList.push(this.$apiService.tasksService.getTask(this.taskData.id))

      Promise.all(requestList)
      .then(results => {

        this.taskTypeList = results[0].data
        this.resourceList = results[1].data
        .filter(resource => resource.isActive && resource.type == ResourceTypes.general)
        .map(resource => {
          return {
            value: resource.id,
            name: resource.name,
            colour: resource.highlightColour
          }
        })
        this.userList = results[2].data
        .filter(user => user.isActive && user.isTaskResource)
        .map(user => {
          return {
            value: user.id,
            name: user.name,
            colour: user.highlightColour
          }
        })

        if(!this.isCreateMode && this.taskData.id) {
          this.taskData = results[3].data

          this.taskData.labelList = this.taskData.labels

          this.selectedUsers = this.taskData.users.map(user => user.id)
          this.selectedResources = this.taskData.resources.filter(resource => resource.type == ResourceTypes.general).map(resource => resource.id)
        }
      })
      .catch(err => {
        this.loadingError = 'Error accessing data'
      })
      .finally(() => {
        this.loading = false

        this.$nextTick(() => {
          
          // Go into edit task mode if requested
          if(this.options && this.options.editTask) {
            if(this.canUpdateTask) {
              this.editing = true
            }
          }
        })
      })
    },

    flagTaskComplete() {
      this.confirmMessage('Flag Task Complete?', '', { html: 'This will close the task and only flag the task as completed.<br />It will <strong>not</strong> mark despatch items as despatch' })
      .then(success => {
        if(success) {
          // Call 'close' action here
          // We don't want to actually complete all the task items (which would result in despatching)
          // We'll just mark the task are 'completed' and do nothing else (eg, close it)
          // In future this will be handled via the app
          this.$apiService.tasksService.closeTask(this.taskData.id).then(response => {
            this.modalClosed({ closed: true })
            this.hide()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error', 'Error while completing task')
          })
        }
      })
    },
    cancelTask() {
      this.confirmMessage('Cancel Task?', '', { html: 'This will cancel the task and will no longer be available for task actions' })
      .then(success => {
        if(success) {
          this.$apiService.tasksService.cancelTask(this.taskData.id).then(response => {
            this.modalClosed({ closed: true })
            this.hide()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error', 'Error while cancelling task')
          })
        }
      })
    },

    displayTaskSheet(options = {}) {
      var params = $.param(options)
      this.displayReportPDF(`/tasks/${this.taskData.id}/runsheet?${params}`)
    }

  }
}
</script>